import { type SerializedStyles } from '@emotion/react';
import tw, { css, styled } from 'twin.macro';
import Icon from '@/components/Icon/IconAsset';
import isPropValid from '@emotion/is-prop-valid';

const blockStyles = css`
  ${tw`transition-colors duration-150 ease-in`}
  ${tw`inline-flex justify-center items-center gap-[4px]`}
  ${tw`whitespace-nowrap`}
  ${tw`rounded-[40px]`}
  ${tw`font-inter font-semi-bold`}
  ${tw`text-text-cta`}
  ${tw`border-solid border-4 border-secondary-s1 hover:border-ui-colours-ui2`}
  ${tw`disabled:hover:cursor-not-allowed`}
  ${tw`[transform-style: preserve-3d]`}
`;

const primaryStyles = css`
  ${blockStyles}
  ${tw`relative`}
  ${tw`bg-secondary-s1 hover:bg-ui-colours-ui2`}
  ${tw`text-neutrals-n1`}

  ${tw`disabled:bg-ui-colours-ui3 disabled:text-neutrals-n1 disabled:border-ui-colours-ui3`}
`;

export const secondaryStyles = css`
  ${blockStyles}
  ${tw`relative`}
  ${tw`text-primary-p1`}
  ${tw`bg-neutrals-n1`}
  ${tw`disabled:text-ui-colours-ui3 disabled:border-ui-colours-ui3`}
`;

const tertiaryStyles = css`
  ${tw`relative`}
  ${tw`inline-flex items-start gap-[4px]`}
  ${tw`font-inter font-semi-bold`}
  ${tw`text-text-cta text-center`}
  ${tw`text-secondary-s1 hover:text-ui-colours-ui2`}
  ${tw`disabled:text-ui-colours-ui3 disabled:hover:cursor-not-allowed`}
`;

const sizeDefaultStyles = css`
  ${tw`py-[11px] px-[20px]`}
`;

const sizeSmallStyles = css`
  ${tw`py-[7px] px-[20px]`}
`;

const sizeTinyStyles = css`
  ${tw`text-tag-s`}
  ${tw`py-[7px] px-[14px]`}
`;

const animatedUnderlineStyles = css`
  ${tw`hover:after:scale-x-100 hover:after:bg-secondary-s1 active:after:scale-x-100 disabled:hover:after:scale-x-0`}

  &:after {
    ${tw`transition-all duration-150`}
    ${tw`absolute`}
    ${tw`origin-left scale-x-0`}
    ${tw`left-0 bottom-0`}
    ${tw`[content: '']`}
    ${tw`w-full h-[4px]`}
    ${tw`bg-ui-colours-ui2`}
  }
  ${tw`font-inter font-semi-bold`}
  ${tw`text-tag-l text-center`}
  ${tw`tracking-[2.4px]`}
  ${tw`text-secondary-s1`}
`;

// NOTE aria-selected is used to determine the active state of the tab
const tabPrimaryStyles = css`
  ${blockStyles}
  ${tw`border-2`}

  // NOT using aria-selected because as the current implementation makes it difficult to drill down props (isActive)
  &[aria-selected='false'] {
    ${tw`bg-ui-colours-ui3`}
    ${tw`border-ui-colours-ui3 `}
    ${tw`text-neutrals-n1 hover:text-ui-colours-ui2`}
  }

  &[aria-selected='true'] {
    ${tw`text-neutrals-n1`}
    ${tw`bg-secondary-s1 hover:bg-ui-colours-ui2`}
  }
`;

export type LinkVariant =
  | 'nav_text_link'
  | 'primary'
  | 'primary_small'
  | 'primary_tiny'
  | 'secondary'
  | 'secondary_small'
  | 'secondary_tiny'
  | 'tertiary'
  | 'tertiary_no_underline'
  | 'tab_primary'
  | 'tab_primary_small';

export const variants: Record<LinkVariant, SerializedStyles> = {
  primary: css`
    ${primaryStyles}
    ${sizeDefaultStyles}
  `,
  primary_small: css`
    ${primaryStyles}
    ${sizeSmallStyles}
  `,
  primary_tiny: css`
    ${primaryStyles}
    ${sizeTinyStyles}
  `,
  secondary: css`
    ${secondaryStyles}
    ${sizeDefaultStyles}
  `,
  secondary_small: css`
    ${secondaryStyles}
    ${sizeSmallStyles}
  `,
  secondary_tiny: css`
    ${secondaryStyles}
    ${sizeTinyStyles}
  `,
  tertiary: css`
    ${tertiaryStyles}
    ${animatedUnderlineStyles}
    ${tw`pb-[15px] pt-[11px]`}
    ${tw`px-0`}
    ${tw`py-[11px]`}
    ${tw`hover:after:scale-x-100`}

    &:after {
      ${tw`transition-all duration-150`}
      ${tw`absolute`}
      ${tw`origin-left scale-x-0`}
      ${tw`left-0 bottom-0`}
      ${tw`[content: '']`}
      ${tw`w-full h-[2px]`}
      ${tw`bg-secondary-s1`}
    }
    ${tw` disabled:text-ui-colours-ui3 disabled:hover:cursor-not-allowed disabled:hover:after:scale-x-0`}
  `,
  tertiary_no_underline: css`
    ${tertiaryStyles}
  `,
  nav_text_link: css`
    ${tertiaryStyles}
    ${animatedUnderlineStyles}
    ${tw`h-full`}
    ${tw`min-h-[51px]`}
    ${tw`items-center`}
    ${tw`text-primary-p1 hover:text-primary-p1`}
  `,
  tab_primary: css`
    ${tabPrimaryStyles}
    ${tw`py-[13px] px-[20px]`}
  `,
  tab_primary_small: css`
    ${tabPrimaryStyles}
    ${tw`py-[9px] px-[20px]`}
  `,
};

export const StyledIcon = styled(Icon)`
  ${tw`inline`}
  ${tw`h-[18px] w-[18px]`}
  ${tw`ml-[10px]`}
`;

export const linkIconStyles = css`
  ${StyledIcon} {
    ${tw`transition-[margin] duration-150 ease-in`}
    ${tw`h-[16px] w-[16px]`}
    ${tw`ml-0`}
  }

  &:hover ${StyledIcon} {
    ${tw`ml-[4px]`}
  }

  &:disabled&:hover ${StyledIcon} {
    ${tw`ml-0`}
  }
`;

export const StyledLink = styled('a', {
  shouldForwardProp: (propName) => isPropValid(propName) && !['variant'].includes(propName),
})<{
  variant?: LinkVariant;
}>`
  ${({ variant }) =>
    variant &&
    css`
      ${variants[variant]}
      ${linkIconStyles}
    `}
`;
